import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { KeyboardEvent, RefObject } from "react";
import {
  findInputTriggerAtCaret,
  removeInputTargetTrigger,
  type InputTrigger,
} from "@/lib/input-trigger";
import type { ComposerFieldHandle } from "./standalone-bar-field";

/** What the mounted "@" popup does for the keys the textarea hands it. */
export interface InputTargetPickerHandle {
  /** Move the highlight one row down (1) or up (-1), clamped to the rows. */
  moveHighlight: (delta: 1 | -1) => void;
  /** Pick the highlighted row once rows are settled and no pick is running. */
  pickHighlighted: () => void;
  /** Pick New chat when the popup offers it; false when it does not. */
  pickNew: () => boolean;
}

export interface InputTargetPicker {
  /** The "@query" being typed while the popup is open; null while closed. */
  trigger: InputTrigger | null;
  popupRef: RefObject<InputTargetPickerHandle | null>;
  /** Returns true when the popup consumed the key. */
  handleKeyDown: (event: KeyboardEvent<HTMLElement>) => boolean;
  /** A pick from `trigger` succeeded: strip its "@query", close, keep focus. */
  completePick: (trigger: InputTrigger) => void;
}

const POPUP_KEYS = new Set(["ArrowDown", "ArrowUp", "Enter", "Escape"]);
/** Keys that complete a typed "@new" the way Enter does. */
const NEW_CHAT_COMPLETION_KEYS = new Set([" ", "Tab"]);

/** "@" target picker for the standalone bar: when the popup is open, and the
 *  textarea keys it owns. Rows, highlight and picking live in the popup, so a
 *  pick error ends with the popup that showed it. */
export function useInputTargetPicker({
  enabled,
  value,
  onChange,
  inputRef,
  chipOffset,
}: {
  enabled: boolean;
  value: string;
  onChange: (value: string) => void;
  inputRef: RefObject<ComposerFieldHandle | null>;
  chipOffset?: number | null;
}): InputTargetPicker {
  const [trigger, setTrigger] = useState<InputTrigger | null>(null);
  const popupRef = useRef<InputTargetPickerHandle>(null);
  const latestValueRef = useRef(value);
  latestValueRef.current = value;

  // Every draft change (typing, desktop paste, dictation, prefill, send)
  // re-reads "@query" at the committed caret, so no query goes stale. The same
  // "@query" at the same place keeps its object, so the bar skips a render.
  useLayoutEffect(() => {
    const next = enabled
      ? findInputTriggerAtCaret(value, inputRef.current, "@", chipOffset)
      : null;
    setTrigger((current) =>
      current &&
      next &&
      current.start === next.start &&
      current.query === next.query
        ? current
        : next,
    );
  }, [chipOffset, enabled, inputRef, value]);

  const completePick = useCallback(
    (picked: InputTrigger) => {
      const latest = latestValueRef.current;
      const next = removeInputTargetTrigger(latest, picked);
      const input = inputRef.current;
      if (input && next !== latest) {
        // Like the desktop paste: commit text and caret before React sees the
        // draft, so the caret stays put instead of jumping to the end. A caret
        // in or at the token lands where it was; one the user moved elsewhere
        // during a slow pick keeps its place in the text.
        const caret = input.selectionStart ?? latest.length;
        const removed = latest.length - next.length;
        const nextCaret =
          caret <= picked.start
            ? caret
            : Math.max(picked.start, caret - removed);
        input.value = next;
        input.setSelectionRange(nextCaret, nextCaret);
      }
      onChange(next);
      setTrigger(null);
      input?.focus();
    },
    [inputRef, onChange],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>): boolean => {
      if (!trigger) return false;
      const composing = event.nativeEvent.isComposing || event.keyCode === 229;
      if (
        NEW_CHAT_COMPLETION_KEYS.has(event.key) &&
        !composing &&
        !event.shiftKey &&
        !event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        trigger.query.toLocaleLowerCase() === "new" &&
        popupRef.current?.pickNew()
      ) {
        event.preventDefault();
        return true;
      }
      if (!POPUP_KEYS.has(event.key)) return false;
      // An IME composes with these keys: leave them to it, and keep the bar
      // from sending or closing on them too.
      if (composing) return true;
      event.preventDefault();
      if (event.key === "Escape") {
        // Close only the popup: the bar's own Escape handlers must not see it.
        event.stopPropagation();
        setTrigger(null);
      } else if (event.key === "Enter") {
        // The popup owns Enter while open: it never sends.
        popupRef.current?.pickHighlighted();
      } else {
        popupRef.current?.moveHighlight(event.key === "ArrowDown" ? 1 : -1);
      }
      return true;
    },
    [trigger],
  );

  return { trigger, popupRef, handleKeyDown, completePick };
}
